import { useEffect, useState } from 'react';
import { authedFetch } from '../../lib/api.ts';
import { FileTypeIcon } from '../../components/icons.tsx';

/**
 * The picture on a tile in the grid.
 *
 * Only photographs and scans have one: the thumbnail is rendered by a
 * background job after an upload, and only for images. Everything else — a
 * PDF, a workbook, a letter — draws its type icon large, which is the honest
 * answer rather than a grey rectangle. A picture of a spreadsheet tells you
 * nothing the word XLSX does not.
 *
 * Fetched rather than pointed at with a `src`, because the route needs an
 * Authorization header and an `<img>` cannot send one. The blob URL is
 * released when the tile goes, or the browser holds every thumbnail in a
 * scrolled folder for the life of the tab.
 */
export function Thumbnail({
  fileId,
  name,
  mimeType,
}: {
  fileId: string;
  name: string;
  mimeType: string;
}) {
  const [url, setUrl] = useState<string | null>(null);

  const drawable = mimeType.startsWith('image/');

  useEffect(() => {
    if (!drawable) return undefined;

    let objectUrl: string | null = null;
    let cancelled = false;

    (async () => {
      try {
        const response = await authedFetch(`/api/drive/files/${fileId}/thumbnail`, {
          credentials: 'include',
        });
        // 404 is the ordinary answer for a file whose thumbnail has not been
        // rendered yet. The icon below is the right thing to show for it.
        if (!response.ok) return;

        objectUrl = URL.createObjectURL(await response.blob());
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setUrl(objectUrl);
      } catch {
        // Same as a 404: nothing to draw, and nothing worth saying about it.
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [fileId, drawable]);

  if (url) {
    return <img src={url} alt="" className="h-full w-full object-cover" />;
  }

  return <FileTypeIcon name={name} mimeType={mimeType} size={44} />;
}
